// The one focus look for a text field on a phone sheet (BUG-2057), shared by
// TextInput, TextArea and NumberInput so the three never drift apart again.

/**
 * The settings-v2 `.ui-input` focus look, as Tailwind classes: a 1px dark-teal
 * border plus the 3px mint glow. Measured per ADR 051 — Contrast Floor (the
 * focus edge identifies state, so it needs 3:1 on the white field):
 *
 *   --primary #09514D on #FFFFFF ........ 9.15:1  non-text ✓ (needs 3.0)
 *   --mint-500 #45D784 on #FFFFFF ....... 1.86:1  the glow alone fails, so the
 *                                                 border carries the state
 *
 * `--shadow-focus` is declared in `settings-v2.css`, which every page that can
 * open a sheet loads (the `.ui-modal--sheet` rules live there too). Where it is
 * missing the glow drops and the 9.15:1 border still shows focus.
 *
 * The `shadow:` type hint is load-bearing. A bare `shadow-[var(--x)]` is read by
 * Tailwind as a shadow COLOUR, so no glow is drawn at all (BUG-2057, QA).
 */
export const SHEET_FIELD_FOCUS = 'focus:outline-none focus:border-primary focus:shadow-[shadow:var(--shadow-focus)]';

/** {@link SHEET_FIELD_FOCUS} for a wrapper that holds the input (a `$` field). */
export const SHEET_FIELD_FOCUS_WITHIN = 'focus-within:border-primary focus-within:shadow-[shadow:var(--shadow-focus)]';

/**
 * BL-2472 — a field in error keeps its red while focused (ADR 030 — Design Canon, "A field with
 * an error stays red while focused"). The red border stays and the glow turns red, the same 3px
 * as the valid glow. `--shadow-error-focus` sits beside `--shadow-focus` in `settings-v2.css`.
 *
 *   red-600 #DC2626 on #FFFFFF .......... 4.83:1  non-text ✓ (needs 3.0); the border carries the state
 */
export const SHEET_FIELD_ERROR_FOCUS =
    'focus:outline-none focus:border-red-600 focus:shadow-[shadow:var(--shadow-error-focus)]';

/** {@link SHEET_FIELD_ERROR_FOCUS} for a wrapper that holds the input (a `$` field). */
export const SHEET_FIELD_ERROR_FOCUS_WITHIN =
    'focus-within:border-red-600 focus-within:shadow-[shadow:var(--shadow-error-focus)]';

/**
 * BL-2472 — the desktop twin: the field's 2px focus ring, in red, over the red border. Every
 * desktop field in error uses it, whatever its valid ring colour.
 */
export const FIELD_ERROR_FOCUS = 'focus:outline-none focus:ring-2 focus:ring-red-600/30 focus:border-red-600';

/** {@link FIELD_ERROR_FOCUS} for a wrapper that holds the input (a `$` field). */
export const FIELD_ERROR_FOCUS_WITHIN = 'focus-within:ring-2 focus-within:ring-red-600/30 focus-within:border-red-600';

/**
 * Pick a field's focus classes: the error look while invalid, else the sheet's or the field's
 * own desktop look. One choice for TextInput, TextArea and NumberInput (BL-2472).
 *
 * @param opts - whether the field is invalid, whether it is on a phone sheet, and its valid desktop focus classes
 * @returns the focus classes for the field
 */
export function fieldFocusClasses(opts: { invalid: boolean; sheet: boolean; desktop: string }): string {
    if (opts.invalid) return opts.sheet ? SHEET_FIELD_ERROR_FOCUS : FIELD_ERROR_FOCUS;
    return opts.sheet ? SHEET_FIELD_FOCUS : opts.desktop;
}

/**
 * {@link fieldFocusClasses} for a wrapper that holds the input (a `$` field).
 *
 * @param opts - whether the field is invalid, whether it is on a phone sheet, and its valid desktop focus-within classes
 * @returns the focus-within classes for the wrapper
 */
export function fieldFocusWithinClasses(opts: { invalid: boolean; sheet: boolean; desktop: string }): string {
    if (opts.invalid) return opts.sheet ? SHEET_FIELD_ERROR_FOCUS_WITHIN : FIELD_ERROR_FOCUS_WITHIN;
    return opts.sheet ? SHEET_FIELD_FOCUS_WITHIN : opts.desktop;
}
